import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {AuditLogActor, AuditLogObjectType, Campaign, ListResult} from 'utils/types';
import {addResultCases} from 'utils/helpers';
import ApiService from 'redux/services/api-service';

export type RevisionModule = {
  value: string;
  label: string;
};

export type VersionDiff = {
  model: string;
  model_verbose_name: string;
  pk: any;
  object_repr: string;
  is_deleted_object: boolean;
  field_diffs: {string: any[]};
  fields: {string: {name: string; previous_version_value: string; version_value: string}};
};

export type RevisionWithVersions = {
  id: number;
  date_created: Date;
  comment: string;
  user: string;
  versions?: {string: VersionDiff};
};

export type CampaignBenchmarkValidationParams = {
  campaign_id: number | string;
  include_all_associated_campaigns: boolean;
  associated_campaign_ids?: number[];
};

export type ClearCampaignDataPayload = {
  campaign_id: number | string;
  bypass_subscriptions?: boolean;
  new_retailer_id?: number | null;
};

export interface IState {
  associatedCampaignObjects: Campaign[];
  logs: {created: string; text: string}[];
  logsCount: number;
  logsLoading: boolean;
  auditLogs: ListResult<any>;
  auditLogActors: ListResult<AuditLogActor>;
  auditLogObjectTypes: ListResult<AuditLogObjectType>;
  revisions: {date_created: string; comment: string; user: string}[];
  revisionsCount: number;
  revisionsLoading: boolean;
  userAccessList: any[];
  userAccessListLoading: boolean;
  userInviteList: any[];
  userInviteListLoading: boolean;
}

export const campaignEndpoints = {
  getAssociatedCampaignObjects: createAsyncThunk('campaign/getAssociatedCampaignObjects', async (campaign_id: any) => {
    const response = await ApiService.get(`/campaigns/associated_campaigns/?campaign_id=${campaign_id}`);
    return response.data;
  }),
  getUserCampaignAccessList: createAsyncThunk('campaign/getUserCampaignAccessList', async (campaign_id: any) => {
    const response = await ApiService.get(`/user_campaign_access/?campaign_id=${campaign_id}`);
    return response.data;
  }),
  getCampaignInviteList: createAsyncThunk('campaign/getCampaignInviteList', async (campaign_id: any) => {
    const response = await ApiService.get(`/campaign_invite/?campaign_id=${campaign_id}`);
    return response.data;
  }),
  deleteCampaignInviteList: createAsyncThunk('campaign/deleteCampaignInviteList', async (campaign_invite_id: any) => {
    const response = await ApiService.delete(`/campaign_invite/${campaign_invite_id}/`);
    return response.data;
  }),

  // Measurement plan
  getAssociatedCampaigns: createAsyncThunk('campaign/getAssociatedCampaigns', async (campaign_id: any) => {
    const response = await ApiService.get(`/campaign_associated_campaigns/?campaign_id=${campaign_id}`);
    return response.data;
  }),
  updateAssociatedCampaigns: createAsyncThunk('campaign/updateAssociatedCampaigns', async (payload: any) => {
    const response = await ApiService.post('/campaign_associated_campaigns/update_bulk/', payload);
    return response.data;
  }),
  updateStoreAttributes: createAsyncThunk('campaign/updateStoreAttributes', async (payload: any) => {
    const response = await ApiService.post('/campaign_store_attributes/update_bulk/', payload);
    return response.data;
  }),
  updateItemAttributes: createAsyncThunk('campaign/updateItemAttributes', async (payload: any) => {
    const response = await ApiService.post('/campaign_item_attributes/update_bulk/', payload);
    return response.data;
  }),
  getStoreAttributes: createAsyncThunk('campaign/getStoreAttributes', async (campaign_id: any) => {
    const response = await ApiService.get(`/campaign_store_attributes/?campaign_id=${campaign_id}`);
    return response.data;
  }),
  getCampaignItemAttributes: createAsyncThunk('campaign/getCampaignItemAttributes', async (campaign_id: any) => {
    const response = await ApiService.get(`/campaign_item_attributes/?campaign_id=${campaign_id}`);
    return response.data;
  }),

  getCampaignLogList: createAsyncThunk('campaign/getCampaignLogList', async (params: any) => {
    const response = await ApiService.get(`/campaign_log/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),

  getAuditLogList: createAsyncThunk('campaign/getAuditLogList', async (params: any) => {
    const response = await ApiService.get(`/audit_log/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),

  getAuditLogActors: createAsyncThunk('campaign/getAuditLogActors', async (params: any) => {
    const response = await ApiService.get(`/audit_log/actors/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),

  getAuditLogObjectTypes: createAsyncThunk('campaign/getAuditLogObjectTypes', async (params: any) => {
    const response = await ApiService.get(`/audit_log/object-types/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),

  getCampaignRevisionList: createAsyncThunk('campaign/getCampaignRevisionList', async (params: any) => {
    const response = await ApiService.get(`/campaign_revision/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),

  getCampaignRevision: createAsyncThunk('campaign/getCampaignRevision', async (params: {id: any; campaign_id: any}) => {
    const response = await ApiService.get(`/campaign_revision/${params.id}/?campaign_id=${params.campaign_id}`);
    return response.data;
  }),

  getRevisionModuleList: createAsyncThunk('campaign/getRevisionModuleList', async () => {
    const response = await ApiService.get(`/revision_module`);
    return response.data;
  }),

  searchCampaignList: createAsyncThunk('campaign/searchCampaignList', async (params: any) => {
    const response = await ApiService.get(`/campaigns/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),
  getCampaign: createAsyncThunk('campaign/getCampaign', async (params: {campaign_id: any}) => {
    const response = await ApiService.get(`/campaigns/${params?.campaign_id}/`);
    return response.data;
  }),
  generateTestControlFindings: createAsyncThunk(
    'campaign/generateTestControlFindings',
    async (campaign_id: number | string, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaigns/${campaign_id}/generate_test_control_findings/`);
        return response.data as {llm_request_id: number; campaign_task_id: number};
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  linkAssociatedCampaign: createAsyncThunk(
    'campaign/linkAssociatedCampaign',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/campaign_associated_campaigns/link_campaign/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  createAssociatedCampaign: createAsyncThunk('campaign/createAssociatedCampaign', async (payload: any) => {
    const response = await ApiService.post('/campaign_associated_campaigns/create_campaign/', payload);
    return response.data;
  }),
  createValassisConfig: createAsyncThunk('campaign/createValassisConfig', async (payload: any) => {
    const response = await ApiService.post('/campaign_valassis_config/', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }),
  getValassisConfig: createAsyncThunk('campaign/getValassisConfig', async (id: any) => {
    const response = await ApiService.get(`/campaign_valassis_config/${id}`);
    return response.data;
  }),
  downloadValassisReport: createAsyncThunk('store/downloadValassisReport', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.get(
        `/campaign_valassis_report/${payload.id}/${payload.campaign_id}/?type=${payload.type}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  updateCampaignImage: createAsyncThunk('campaign/updateCampaignImage', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post(`/update_campaign_image/`, payload.form_data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  resetCampaignImage: createAsyncThunk('campaign/resetCampaignImage', async (payload: any) => {
    const response = await ApiService.delete(
      `/update_campaign_image/?campaign_id=${payload.campaign_id}&type=${payload.type}&entity_id=${payload.entity_id}`,
    );
    return response.data;
  }),
  updateCampaignReportTemplate: createAsyncThunk(
    'campaign/updateCampaignReportTemplate',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/update_campaign_report_template/`, payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  transferCampaignOwnership: createAsyncThunk(
    'campaign/transferCampaignOwnership',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/campaigns/transfer_campaign_ownership/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  validateCampaignBenchmark: createAsyncThunk(
    'campaign/validateCampaignBenchmark',
    async ({campaign_id, ...params}: CampaignBenchmarkValidationParams, {rejectWithValue}) => {
      const urlParams = new URLSearchParams();
      urlParams.append('include_all_associated_campaigns', String(params.include_all_associated_campaigns));
      if (params.associated_campaign_ids && params.associated_campaign_ids.length > 0) {
        params.associated_campaign_ids.forEach(id => urlParams.append('associated_campaign_ids', String(id)));
      }
      let qs = urlParams.toString();
      qs = qs ? `?${qs}` : '';
      try {
        const response = await ApiService.get(`/campaigns/${campaign_id}/campaign_benchmark/${qs}`);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  toggleCampaignLocked: createAsyncThunk(
    'campaign/toggleCampaignLocked',
    async (payload: {campaign_id: number | string; run_cleanup?: boolean}, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaigns/${payload.campaign_id}/toggle_locked/`, {
          run_cleanup: payload.run_cleanup ?? false,
        });
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  runCampaignCleanup: createAsyncThunk(
    'campaign/runCampaignCleanup',
    async (campaign_id: number | string, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaigns/${campaign_id}/run_cleanup/`);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  clearCampaignData: createAsyncThunk(
    'campaign/clearCampaignData',
    async ({campaign_id, ...payload}: ClearCampaignDataPayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaigns/${campaign_id}/clear_data/`, payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  createCampaignBenchmark: createAsyncThunk(
    'campaign/createCampaignBenchmark',
    async ({campaign_id, ...params}: CampaignBenchmarkValidationParams, {rejectWithValue}) => {
      const payload = {...params};
      try {
        const response = await ApiService.post(`/campaigns/${campaign_id}/campaign_benchmark/`, payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
};
export const campaignSlice = createSlice({
  name: 'campaign',
  initialState: {
    logs: [],
    logsLoading: false,
    logsCount: 0,
    auditLogs: {as: 'idle'},
    auditLogActors: {as: 'idle'},
    auditLogObjectTypes: {as: 'idle'},
    revisions: [],
    revisionsLoading: false,
    revisionsCount: 0,
    associatedCampaignObjects: [],
    userAccessList: [],
    userAccessListLoading: false,
    userInviteList: [],
    userInviteListLoading: false,
  } as IState,
  reducers: {
    clearAssociatedCampaignObjects(state) {
      state.associatedCampaignObjects = [];
    },
  },
  extraReducers: builder => {
    addResultCases(builder, campaignEndpoints.getAuditLogList, 'auditLogs');
    addResultCases(builder, campaignEndpoints.getAuditLogActors, 'auditLogActors');
    addResultCases(builder, campaignEndpoints.getAuditLogObjectTypes, 'auditLogObjectTypes');
    builder

      .addCase(campaignEndpoints.getAssociatedCampaignObjects.fulfilled, (state, {payload}) => {
        state.associatedCampaignObjects = payload;
      })
      .addCase(campaignEndpoints.getUserCampaignAccessList.pending, state => {
        state.userAccessListLoading = true;
      })
      .addCase(campaignEndpoints.getUserCampaignAccessList.fulfilled, (state, {payload}) => {
        state.userAccessListLoading = false;
        state.userAccessList = payload.results;
      })
      .addCase(campaignEndpoints.getUserCampaignAccessList.rejected, state => {
        state.userAccessListLoading = false;
      })

      .addCase(campaignEndpoints.getCampaignInviteList.pending, state => {
        state.userInviteListLoading = true;
      })
      .addCase(campaignEndpoints.getCampaignInviteList.fulfilled, (state, {payload}) => {
        state.userInviteListLoading = false;
        state.userInviteList = payload.results;
      })
      .addCase(campaignEndpoints.getCampaignInviteList.rejected, state => {
        state.userInviteListLoading = false;
      })
      .addCase(campaignEndpoints.getCampaignLogList.pending, state => {
        state.logsLoading = true;
      })
      .addCase(campaignEndpoints.getCampaignLogList.fulfilled, (state, {payload}) => {
        state.logsLoading = false;
        state.logs = payload.results;
        state.logsCount = payload.count;
      })
      .addCase(campaignEndpoints.getCampaignLogList.rejected, state => {
        state.logsLoading = false;
      })
      .addCase(campaignEndpoints.getCampaignRevisionList.pending, state => {
        state.revisionsLoading = true;
      })
      .addCase(campaignEndpoints.getCampaignRevisionList.fulfilled, (state, {payload}) => {
        state.revisionsLoading = false;
        state.revisions = payload.results;
        state.revisionsCount = payload.count;
      })
      .addCase(campaignEndpoints.getCampaignRevisionList.rejected, state => {
        state.revisionsLoading = false;
      });
  },
});

export const campaignActions = campaignSlice.actions;

export type CampaignSlice = {
  [campaignSlice.name]: ReturnType<(typeof campaignSlice)['reducer']>;
};
